import { LoadingOutlined } from '@ant-design/icons';
import { useSessionContext } from '@/contexts/session/context';
import Paths from '@/configs/router/paths';
import { userApis } from '@/configs/apis/user';
import styles from './index.module.scss';
import LoginMethodItem from './components/LoginMethodItem';
import {
  appleLoginWeb,
  getDeviceId,
  getDeviceType,
  googleLoginWeb,
  isDesktop,
} from './helpers';
import type { LoginMethod } from './interface';

const loginMethods: LoginMethod[] = [
  {
    value: 'Apple',
    label: 'Apple account',
    securityLevel: 'Ordinary',
    subText: 'Create wallet using an Apple account.',
    warning: 'Apple ID is not supported in mainland China now.',
  },
  {
    value: 'Google',
    label: 'Google account',
    securityLevel: 'Ordinary',
    subText: 'Create wallet using a Google account',
  },
];

const Login = () => {
  const getLoginMethod = () => {
    const loginMethod = localStorage.getItem('loginMethod');
    if (loginMethod) {
      return JSON.parse(loginMethod);
    }
    return loginMethods.find((method) => method.value === 'Google');
  };
  const [open, setOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<LoginMethod | null>(
    getLoginMethod()
  );
  const [loading, setLoading] = useState(false);
  const deferredOpen = useDeferredValue(open);
  const navigate = useNavigate();
  const { login } = useSessionContext();

  const spinning = useMemo(() => {
    return loading;
  }, [loading]);

  const onSelect = useCallback(
    (method: LoginMethod) => {
      navigate(Paths.LOGIN);
      setSelectedMethod(method);
      localStorage.setItem('loginMethod', JSON.stringify(method));
      setOpen(false);
    },
    [navigate]
  );

  const userLogin = useCallback(
    async (params: any) => {
      try {
        setLoading(true);
        const res = await userApis.login(params);
        await login(res);
        // await requestNotificationPermission();
        navigate(Paths.MESSAGES_CHATS);
      } catch (error) {
        console.error('Error logging in:', error);
      } finally {
        setLoading(false);
      }
    },
    [login, navigate]
  );

  const handleLoginClick = useCallback(async () => {
    if (!selectedMethod) {
      return message.error('Please select a login method');
    }
    if (selectedMethod?.value === 'Google') {
      googleLoginWeb();
    }
    if (selectedMethod?.value === 'Apple') {
      appleLoginWeb();
    }
  }, [selectedMethod]);

  const checkLogin = useCallback(async () => {
    if (userApis.isLogin()) {
      return navigate(Paths.MESSAGES_CHATS);
    }
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const scope = urlParams.get('scope');
    const dev_user = urlParams.get('dev_user');
    if (scope) {
      userLogin({
        token: code,
        login_type: isDesktop() ? 'google_client_v2' : 'google_web_v2',
        device_id: getDeviceId(),
        device_type: getDeviceType(),
        push_token: '',
      });
    } else if (code) {
      userLogin({
        token: code,
        login_type: 'apple_web',
        device_id: getDeviceId(),
        device_type: getDeviceType(),
      });
    }
    // TODO amo: Dev arg
    if (dev_user) {
      userLogin({
        token: dev_user,
        login_type: 'dev',
        device_id: getDeviceId(),
        device_type: getDeviceType(),
        push_token: '',
      });
    }
  }, [navigate, userLogin]);

  useEffect(() => {
    checkLogin();
  }, [checkLogin, navigate]);

  return (
    <Spin spinning={spinning} indicator={<LoadingOutlined spin />}>
      <div className={styles.login}>
        <div className={styles.loginBanner}>
          <div className={styles.loginBannerInner}></div>
        </div>
        <div className={styles.loginBar}>
          <div className={styles.loginBarTitle}>Welcome to Halo chat</div>
          <div className={styles.loginBarForm}>
            <Popover
              open={deferredOpen}
              onOpenChange={setOpen}
              trigger="click"
              getPopupContainer={(triggerNode) => triggerNode.parentElement!}
              content={loginMethods.map((item) => (
                <LoginMethodItem
                  key={item.value}
                  item={item}
                  isSelected={selectedMethod?.value === item.value}
                  onSelect={onSelect}
                />
              ))}
            >
              <div className={styles.loginMethodBar}>
                <Flex justify="space-between" align="center">
                  <div>
                    {selectedMethod ? (
                      <>
                        <div className={styles.label}>{selectedMethod?.label}</div>
                        <div className={styles.subText}>{selectedMethod?.subText}</div>
                      </>
                    ) : (
                      <>
                        <div className={styles.label}>Login Method</div>
                        <div className={styles.subText}>
                          Please select your preferred login method
                        </div>
                      </>
                    )}
                  </div>
                  {open ? <UpOutlined /> : <DownOutlined />}
                </Flex>
              </div>
            </Popover>
            <Button type="primary" onClick={handleLoginClick}>
              Login
            </Button>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default Login;
